import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, ArrowRight, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Layout } from '../components/Layout';
import { CartItem } from '../components/CartItem';
import { useCart } from '../context/CartContext';

const CartPage = () => {
  const { cart, clearCart, loading } = useCart();

  if (cart.items.length === 0) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center" data-testid="empty-cart">
            <ShoppingBag className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h1 className="text-2xl font-serif font-bold mb-4">Votre panier est vide</h1>
            <p className="text-muted-foreground mb-6">
              Découvrez notre carte et ajoutez vos plats préférés.
            </p>
            <Link to="/menu">
              <Button className="rounded-full" data-testid="browse-menu-btn">
                Voir la carte
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="cart-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-serif font-bold">Votre Panier</h1>
          <Button
            variant="outline"
            className="rounded-full text-destructive hover:text-destructive"
            onClick={clearCart}
            disabled={loading}
            data-testid="clear-cart-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Vider le panier
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item, idx) => (
              <CartItem key={`${item.item_id}-${idx}`} item={item} />
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-muted rounded-2xl p-6 sticky top-24" data-testid="order-summary">
              <h2 className="text-xl font-serif font-semibold mb-6">Récapitulatif</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Sous-total</span>
                  <span className="font-medium">{cart.total.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Livraison</span>
                  <span className="text-muted-foreground">Calculée après l'adresse</span>
                </div>
              </div>

              <div className="border-t border-border pt-4 mb-6">
                <div className="flex justify-between">
                  <span className="font-semibold">Total estimé</span>
                  <span className="font-bold text-primary text-xl">
                    {cart.total.toFixed(2)} €
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Hors frais de livraison
                </p>
              </div>

              <Link to="/checkout">
                <Button 
                  className="w-full rounded-full bg-primary hover:bg-primary/90" 
                  size="lg"
                  data-testid="proceed-to-checkout"
                >
                  Passer la commande
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>

              <Link to="/menu" className="block mt-4">
                <Button variant="outline" className="w-full rounded-full">
                  Continuer les achats
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CartPage;
